import { Injectable, Injector, isDevMode } from '@angular/core'
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router'
import { fetchAuthSession } from 'aws-amplify/auth'
import { v4 as uuidv4 } from 'uuid'
import { environment } from '../../../environments/environment'
import { APP_VERSION } from '../version/app-version'
import { UserService } from '../services/user.service'
import { isFormFieldTarget } from '../../shared/utils/keyboard-guards'
import { ACTIVITY_EVENTS, type ActivityEventName, type ActivityPropsOf, type TrackableEventName } from './activity-catalogue'
import { BROWSER_CLOCK, type ActivityClock } from './activity-clock'
import { buildEntries, type SerialisedItem } from './activity-batcher'
import { IdbOutboxStore, UnloadJournal, type ActivityOutboxStore } from './activity-outbox'
import { sanitizeProps } from './activity-props'
import { ActivitySender, type ActivityToken } from './activity-sender'
import { isActivationKeyOnControl, normalizeShortcut, registeredShortcut, SURFACE_SHORTCUTS } from './activity-shortcuts'
import { VIA, type ActivityTrigger } from './activity-trigger'
import { TickAccumulator, type InputCounter, type TickContext } from './tick-accumulator'
import {
  ACTIVITY_BUFFER_MAX_EVENTS, ACTIVITY_FLUSH_INTERVAL_MS, ACTIVITY_FLUSH_MAX_BYTES, ACTIVITY_FLUSH_MAX_EVENTS,
  ACTIVITY_FORM_FIELD_IDLE_MS, ACTIVITY_FORM_FIELD_RATE_MS, ACTIVITY_SESSION_DRIFT_MS, ACTIVITY_SESSION_HIDDEN_GAP_MS,
  ACTIVITY_SIGN_OUT_DRAIN_MS, ACTIVITY_SLOW_WAIT_MS, ACTIVITY_TICK_MS,
  type ActivityEventItem, type ActivityModalName, type ActivityProps, type ActivitySurface, type ActivityTaskType,
  type ActivityTickItem,
} from './activity.model'

type SessionReason = ActivityPropsOf<'session.start'>['reason']

interface Session { id: string; userId: string; seq: number; drift0: number }
interface Frame { trigger: ActivityTrigger; token: number | null }
interface KeyDispatch { name: string; token: number }
/** What a route says about tracking: its surface, its task, and whether it handles the surface's shortcuts. */
interface RouteContext { surface: ActivitySurface; taskId: string | null; shortcuts: boolean }
export interface ActivityModalHandle { name: ActivityModalName; startMono: number }

const TASK_TYPE_OF: Partial<Record<ActivitySurface, ActivityTaskType>> = {
  segment_editing: 'segment_editing', markup: 'geometry_markup', quick_edit: 'quick_editing',
}
const MODIFIER_KEYS = new Set(['Shift', 'Control', 'Alt', 'Meta', 'AltGraph', 'CapsLock', 'Dead', 'Process'])

/** Test seams; production uses the defaults. */
export interface ActivityTrackerSeams {
  clock?: ActivityClock
  store?: ActivityOutboxStore
  journal?: UnloadJournal
  fetchFn?: typeof fetch
  getToken?: (force: boolean) => Promise<ActivityToken | null>
}

async function amplifyToken(force: boolean): Promise<ActivityToken | null> {
  const session = await fetchAuthSession({ forceRefresh: force })
  const idToken = session.tokens?.idToken
  if (!idToken) return null
  const exp = Number(idToken.payload['exp'])
  return { token: idToken.toString(), userId: String(idToken.payload['sub']), expMs: exp * 1000 }
}

function detectBrowser(): ActivityPropsOf<'session.start'>['browser'] {
  const ua = navigator.userAgent
  if (/Edg\//.test(ua)) return 'edge'
  if (/Firefox\//.test(ua)) return 'firefox'
  if (/Chrome\//.test(ua)) return 'chrome'
  if (/Safari\//.test(ua)) return 'safari'
  return 'other'
}

/**
 * Editor Activity Tracking (client): the facade every instrumented component
 * talks to. Active only while the current route carries `data.activitySurface`
 * and the signed-in user holds the Editor role; everywhere else every hook is a
 * cheap no-op. The constructor injects nothing — until `init()` runs (only from
 * `provideAppInitializer`) the instance is inert, which keeps every existing
 * component spec working with no new providers.
 *
 * The app is zoneless and nothing here owns a signal a template reads: all
 * listeners are native `addEventListener`, so the tracker never schedules change
 * detection. See CLAUDE.md "Editor Activity Tracking (client)".
 */
@Injectable({ providedIn: 'root' })
export class ActivityTrackerService {
  private initialised = false
  private active = false
  private signedOut = false

  private router?: Router
  private userService?: UserService
  private clock: ActivityClock = BROWSER_CLOCK
  private store: ActivityOutboxStore = new IdbOutboxStore()
  private journal = new UnloadJournal()
  private sender?: ActivitySender
  private readonly disabledUserIds = new Set<string>()

  /** `shortcuts`: false on a route marked `activityRouteData(…, { shortcuts: false })`, where no key is a shortcut. */
  private ctx: (TickContext & { taskType: ActivityTaskType | null; shortcuts: boolean }) | null = null
  private session: Session | null = null
  private events: SerialisedItem[] = []
  private ticks: SerialisedItem[] = []
  private bufferBytes = 0
  private readonly acc = new TickAccumulator()
  private visible = true
  private hiddenWall: number | null = null

  // ambient trigger frames and the current keydown dispatch (§3.1, §3.3)
  private frames: Frame[] = []
  private frameCounter = 0
  private keyDispatch: KeyDispatch | null = null
  private dispatchTracked = false
  private dispatchReported = false
  private repeatCode: string | null = null
  private pointerOverMap = false
  private lastFormFieldKeyMono = -Infinity
  private lastFormFieldEventMono = -Infinity

  private removers: (() => void)[] = []
  private tickTimer: ReturnType<typeof setTimeout> | null = null
  private flushTimer: ReturnType<typeof setInterval> | null = null
  private drainTimer: ReturnType<typeof setInterval> | null = null
  private longTaskObserver: PerformanceObserver | null = null

  // ── Lifecycle ───────────────────────────────────────────────────────────────

  /** Called only by `provideAppInitializer`. Synchronous; awaits nothing. */
  init(injector: Injector, seams: ActivityTrackerSeams = {}): void {
    if (this.initialised) return
    this.initialised = true
    this.signedOut = false
    this.clock = seams.clock ?? this.clock
    this.store = seams.store ?? this.store
    this.journal = seams.journal ?? this.journal
    this.router = injector.get(Router)
    this.userService = injector.get(UserService)
    this.sender = new ActivitySender({
      url: `${environment.apiUrl}/activity/events`,
      appVersion: APP_VERSION,
      fetchFn: seams.fetchFn ?? ((...a) => fetch(...a)),
      clock: this.clock,
      getToken: seams.getToken ?? amplifyToken,
      viewport: () => ({
        w: Math.round(window.innerWidth), h: Math.round(window.innerHeight),
        dpr: Math.min(8, Math.max(0.25, window.devicePixelRatio || 1)),
      }),
      dev: isDevMode(),
    }, this.store, this.journal, {
      onOwnerMismatch: id => { if (this.session?.id === id && this.active) this.rollover('owner_mismatch') },
      onForbidden: userId => {
        this.disabledUserIds.add(userId)
        if (this.session?.userId === userId && this.active) this.deactivate()
      },
    })
    this.router.events.subscribe(ev => { if (ev instanceof NavigationEnd) this.onNavigationEnd() })
    void this.journal.migrateInto(this.store, () => !!this.sender?.isHalted).catch(() => undefined)
  }

  /** True while events are being recorded. Cheap; read by the interceptor and the hooks. */
  get isActive(): boolean { return this.active && !this.signedOut }
  /** The surface being tracked, or null when inactive. */
  get surface(): ActivitySurface | null { return this.isActive ? this.ctx?.surface ?? null : null }
  /** Monotonic ms for durations (`ms` props). */
  mono(): number { return this.clock.mono() }

  private routeContext(): RouteContext | null {
    let snap: ActivatedRouteSnapshot | null = this.router?.routerState.snapshot.root ?? null
    while (snap?.firstChild) snap = snap.firstChild
    const surface = snap?.data['activitySurface'] as ActivitySurface | undefined
    if (!snap || !surface) return null
    const taskId = TASK_TYPE_OF[surface] ? (snap.params['taskId'] as string | undefined) ?? null : null
    return { surface, taskId, shortcuts: snap.data['activityShortcuts'] !== false }
  }

  /** The registered shortcut a key press names on the current route, or null (always null where the route has none). */
  private shortcutOf(e: KeyboardEvent, ctx: { surface: ActivitySurface; shortcuts: boolean }): string | null {
    return ctx.shortcuts ? registeredShortcut(e, ctx.surface) : null
  }

  private onNavigationEnd(): void {
    if (this.signedOut) return
    const next = this.routeContext()
    const user = this.userService?.currentUser
    const eligible = !!next && !!user && user.roles.includes('Editor') && !this.disabledUserIds.has(user.id)
    if (!eligible) { if (this.active) this.deactivate(); return }
    if (this.active && this.ctx && this.ctx.surface === next.surface && this.ctx.taskId === next.taskId) {
      // Same surface, another screen of it (the Inspections list → a project): no route.change, but its own shortcuts.
      this.ctx = { ...this.ctx, shortcuts: next.shortcuts }
      return
    }
    if (this.active) this.switchContext(next)
    else this.activate(next, user.id)
  }

  private newCtx(next: RouteContext) {
    return { ...next, taskType: next.taskId ? TASK_TYPE_OF[next.surface] ?? null : null }
  }

  private activate(next: RouteContext, userId: string): void {
    this.active = true
    this.visible = document.visibilityState !== 'hidden'
    this.installListeners()
    if (this.session && this.session.userId !== userId) this.rollover('user_change', false)
    else if (this.session && this.driftExceeded()) this.rollover('clock_drift', false)
    if (!this.session) this.startSession('page_load', userId)
    const from = this.ctx?.surface ?? 'other'
    this.ctx = this.newCtx(next)
    this.record('route.change', { props: { from, to: next.surface } })
    if (this.visible) { this.openWindow(); this.startTimers() }
    void this.sender?.drain(userId)
  }

  private switchContext(next: RouteContext): void {
    const from = this.ctx!.surface
    this.closeTick()
    this.ctx = this.newCtx(next)
    this.record('route.change', { props: { from, to: next.surface } })
    if (this.visible) this.openWindow()
  }

  private deactivate(): void {
    if (!this.active) return
    this.record('route.change', { props: { from: this.ctx?.surface ?? 'other', to: 'other' } })
    this.closeTick()
    this.stopTimers()
    this.removeListeners()
    const userId = this.session?.userId
    const flushed = this.flushBuffer()
    this.active = false
    this.ctx = null
    this.frames = []
    this.keyDispatch = null
    if (userId) void flushed.then(() => this.sender?.drain(userId))
  }

  private startSession(reason: SessionReason, userId: string): void {
    this.session = { id: uuidv4(), userId, seq: 0, drift0: this.clock.wall() - this.clock.t() }
    this.acc.resetSession(this.clock.t())
    this.recordRaw('session.start', 'app', { reason, browser: detectBrowser(), touch: navigator.maxTouchPoints > 0 })
  }

  private driftExceeded(): boolean {
    if (!this.session) return false
    return Math.abs(this.clock.wall() - this.clock.t() - this.session.drift0) > ACTIVITY_SESSION_DRIFT_MS
  }

  /** New session: the old buffer goes to the outbox under the old identity first. */
  private rollover(reason: SessionReason, reopen = true): void {
    const userId = this.userService?.currentUser?.id ?? this.session?.userId
    if (!userId) return
    this.closeTick()
    void this.flushBuffer()
    this.startSession(reason, userId)
    if (reopen && this.active && this.visible) this.openWindow()
  }

  // ── Sign-out latch ──────────────────────────────────────────────────────────

  /** Explicit logout: flush, one bounded drain with the still-valid token, then purge everything. */
  async onSignOut(): Promise<void> {
    if (!this.initialised || this.signedOut) return
    this.signedOut = true
    try {
      const userId = this.session?.userId
      this.closeTick()
      await this.flushBuffer(true)
      if (userId && this.sender) {
        await Promise.race([
          this.sender.drain(userId).catch(() => undefined),
          new Promise<void>(resolve => setTimeout(resolve, ACTIVITY_SIGN_OUT_DRAIN_MS)),
        ])
      }
    } finally {
      this.sender?.halt() // an in-flight drain must not re-create what the purge removes
      await this.purgeAll()
      this.shutdown()
    }
  }

  /** `authGuard` found no valid session: latch and purge at once, without draining (no valid token). */
  onSessionLost(): void {
    if (!this.initialised || this.signedOut) return
    this.signedOut = true
    this.events = []; this.ticks = []; this.bufferBytes = 0
    this.sender?.halt()
    void this.purgeAll()
    this.shutdown()
  }

  private async purgeAll(): Promise<void> {
    this.journal.purgeAll()
    await this.store.clear().catch(() => undefined)
  }

  private shutdown(): void {
    this.stopTimers()
    this.removeListeners()
    this.active = false
    this.ctx = null
    this.frames = []
    this.keyDispatch = null
    this.events = []; this.ticks = []; this.bufferBytes = 0
  }

  // ── Recording ───────────────────────────────────────────────────────────────

  /**
   * Records an event. No-op when inactive. The trigger is the explicit `opts.trigger`,
   * else the innermost ambient frame, else the catalogue default; a via the event does
   * not declare falls back to the default, so a key frame never stamps `key` on an
   * event that is by nature `auto` or `map`.
   */
  track<N extends TrackableEventName>(name: N, opts?: { trigger?: ActivityTrigger; props?: ActivityPropsOf<N> }): void {
    if (!this.isActive || !this.ctx) return
    const spec = ACTIVITY_EVENTS[name]
    if (!(spec.surfaces as readonly ActivitySurface[]).includes(this.ctx.surface)) return
    if (this.keyDispatch) this.dispatchTracked = true
    this.record(name, opts)
  }

  /** Runs `fn` with `trigger` as the ambient trigger for its synchronous duration. */
  runWith<T>(trigger: ActivityTrigger, fn: () => T): T {
    const frame: Frame = { trigger, token: null }
    this.frames.push(frame)
    try { return fn() } finally {
      const i = this.frames.lastIndexOf(frame)
      if (i >= 0) this.frames.splice(i, 1)
    }
  }

  /** The `{ via: 'key', key }` trigger of a key event (for keyup sites). */
  keyTriggerOf(e: KeyboardEvent): ActivityTrigger {
    const reg = this.ctx ? SURFACE_SHORTCUTS[this.ctx.surface] : SURFACE_SHORTCUTS.markup
    return { via: 'key', key: normalizeShortcut(e, reg.mode, reg.ignoresShift) ?? 'Unidentified' }
  }

  /** `trigger` if given, else the innermost ambient frame's trigger (resolve now, apply later — a pending action keeps how it was asked for). */
  resolveAmbient(trigger?: ActivityTrigger): ActivityTrigger | undefined {
    if (!this.isActive) return trigger
    return trigger ?? this.frames[this.frames.length - 1]?.trigger
  }

  /** The via of the innermost ambient frame (`'key'` inside a registered key's dispatch), or null. */
  ambientVia(): ActivityTrigger['via'] | null {
    return this.isActive ? this.frames[this.frames.length - 1]?.trigger.via ?? null : null
  }

  /** A handler that acts on keydown but reports at keyup (Space-pan, Peek) marks the press as consumed. */
  consumeKey(_e?: KeyboardEvent): void {
    if (this.keyDispatch) this.dispatchTracked = true
  }

  /**
   * Reports a registered shortcut that was refused (`modal`, `menu_open`, `repeat`).
   * Registry-filtered: an unregistered key is never reported, so no key identity
   * leaks that the surface does not define.
   */
  shortcutIgnored(e: KeyboardEvent, reason: 'modal' | 'menu_open' | 'repeat'): void {
    if (!this.isActive || !this.ctx) return
    const key = this.shortcutOf(e, this.ctx)
    if (!key) return
    // one report per hold, whatever the reason: a held key under a modal must not flood
    if (this.repeatCode === e.code) return
    this.repeatCode = e.code
    this.dispatchReported = true
    this.track('shortcut.ignored', { trigger: { via: 'key', key }, props: { reason } })
  }

  /** Counts an input (the map probe's gestures). */
  count(counter: InputCounter): void {
    if (this.isActive) this.acc.input(this.clock.t(), counter)
  }

  setPointerOverMap(over: boolean): void { this.pointerOverMap = over }

  /** A completed API request (the interceptor). */
  recordRequest(kind: string, ms: number, status: number): void {
    if (!this.isActive) return
    this.acc.addCompletion({ requests: 1, waitMs: Math.max(0, Math.round(ms)) })
    if (ms >= ACTIVITY_SLOW_WAIT_MS) this.track('wait.slow', { props: { kind, ms: Math.round(ms), status } })
    if (status === 0 || status >= 400) this.track('request.error', { props: { kind, status } })
  }

  modalOpened(name: ActivityModalName): ActivityModalHandle | null {
    if (!this.isActive) return null
    this.track('modal.open', { props: { name } })
    return { name, startMono: this.clock.mono() }
  }

  modalClosed(handle: ActivityModalHandle | null): void {
    if (!handle) return
    this.track('modal.close', { props: { name: handle.name, ms: Math.round(this.clock.mono() - handle.startMono) } })
  }

  private resolveTrigger(name: ActivityEventName, explicit: ActivityTrigger | undefined): { via: ActivityTrigger['via'] | null; key: string | null } | null {
    const spec = ACTIVITY_EVENTS[name]
    const declared = spec.vias as readonly string[]
    if (declared.length === 0) return { via: null, key: null }
    const candidate = explicit ?? this.frames[this.frames.length - 1]?.trigger
    if (candidate && declared.includes(candidate.via) && (candidate.via !== 'key' || !!candidate.key)) {
      return { via: candidate.via, key: candidate.via === 'key' ? candidate.key ?? null : null }
    }
    // `key` is never a fallback: it needs a key name. Another declared via, or the event is dropped.
    if (spec.defaultVia === 'key') {
      const other = (declared as readonly ActivityTrigger['via'][]).find(v => v !== 'key')
      return other ? { via: other, key: null } : null
    }
    return { via: spec.defaultVia, key: null }
  }

  private record<N extends ActivityEventName>(name: N, opts?: { trigger?: ActivityTrigger; props?: ActivityPropsOf<N> }): void {
    if (!this.ctx) return
    const resolved = this.resolveTrigger(name, opts?.trigger)
    if (!resolved) return
    const { via, key } = resolved
    this.push(name, this.ctx.surface, this.ctx.taskId, this.ctx.taskType, via, key, opts?.props as ActivityProps | undefined)
  }

  private recordRaw<N extends ActivityEventName>(name: N, surface: ActivitySurface, props: ActivityPropsOf<N>): void {
    this.push(name, surface, null, null, null, null, props as ActivityProps)
  }

  private push(
    name: string, surface: ActivitySurface, taskId: string | null, taskType: ActivityTaskType | null,
    via: ActivityEventItem['via'], key: string | null, props: ActivityProps | undefined,
  ): void {
    if (!this.session) return
    const item: ActivityEventItem = {
      eventId: uuidv4(), seq: this.session.seq++, t: this.clock.t(), name, surface, taskId, taskType, via, key,
      props: sanitizeProps(props),
    }
    const json = JSON.stringify(item)
    this.events.push({ json, t: item.t })
    this.bufferBytes += json.length
    if (this.events.length > ACTIVITY_BUFFER_MAX_EVENTS) {
      const dropped = this.events.shift()!
      this.bufferBytes -= dropped.json.length
    }
    if (this.events.length >= ACTIVITY_FLUSH_MAX_EVENTS || this.bufferBytes >= ACTIVITY_FLUSH_MAX_BYTES) this.flushAndDrain()
  }

  // ── Ticks ───────────────────────────────────────────────────────────────────

  private openWindow(): void {
    if (!this.ctx || this.acc.isOpen) return
    this.acc.openWindow(this.clock.t(), { surface: this.ctx.surface, taskId: this.ctx.taskId }, this.visible)
  }

  private closeTick(): void {
    const tick: ActivityTickItem | null = this.acc.close(this.clock.t(), document.hasFocus())
    if (!tick) return
    const json = JSON.stringify(tick)
    this.ticks.push({ json, t: tick.t })
    this.bufferBytes += json.length
  }

  private startTimers(): void {
    this.stopTimers()
    const tick = () => {
      this.closeTick()
      if (this.driftExceeded()) this.rollover('clock_drift', false)
      this.openWindow()
      this.tickTimer = setTimeout(tick, ACTIVITY_TICK_MS)
    }
    this.tickTimer = setTimeout(tick, ACTIVITY_TICK_MS)
    this.flushTimer = setInterval(() => this.flushAndDrain(), ACTIVITY_FLUSH_INTERVAL_MS)
    this.drainTimer = setInterval(() => {
      const userId = this.session?.userId
      if (userId) void this.sender?.hasDue(userId).then(due => { if (due) void this.sender?.drain(userId) })
    }, 60_000)
  }

  private stopTimers(): void {
    if (this.tickTimer) clearTimeout(this.tickTimer)
    if (this.flushTimer) clearInterval(this.flushTimer)
    if (this.drainTimer) clearInterval(this.drainTimer)
    this.tickTimer = this.flushTimer = this.drainTimer = null
  }

  // ── Flushing ────────────────────────────────────────────────────────────────

  private takeEntries() {
    const s = this.session
    if (!s || this.events.length + this.ticks.length === 0) return []
    const entries = buildEntries(this.events, this.ticks, {
      userId: s.userId, sessionId: s.id, wall: this.clock.wall(), newId: uuidv4,
    })
    this.events = []; this.ticks = []; this.bufferBytes = 0
    return entries
  }

  /** Moves the buffer into the outbox (entries built synchronously, stored asynchronously). */
  private async flushBuffer(duringSignOut = false): Promise<void> {
    const entries = this.takeEntries()
    for (const e of entries) {
      if (this.signedOut && !duringSignOut) return // the purge has latched: never write after it
      await this.store.put(e).catch(() => undefined)
    }
  }

  private flushAndDrain(): void {
    const userId = this.session?.userId
    void this.flushBuffer().then(() => { if (userId && this.active) void this.sender?.drain(userId) })
  }

  /** The hidden/close path: synchronous journal write, then a keepalive send. Never touches IndexedDB. */
  private flushSync(): void {
    if (this.signedOut) return
    for (const e of this.takeEntries()) {
      this.journal.append(e)
      this.sender?.sendKeepalive(e)
    }
  }

  // ── Listeners ───────────────────────────────────────────────────────────────

  private listen<K extends keyof WindowEventMap>(target: Window, type: K, fn: (e: WindowEventMap[K]) => void, opts?: AddEventListenerOptions): void
  private listen(target: Document | Window, type: string, fn: (e: never) => void, opts?: AddEventListenerOptions): void
  private listen(target: Document | Window, type: string, fn: (e: never) => void, opts?: AddEventListenerOptions): void {
    target.addEventListener(type, fn as EventListener, opts)
    this.removers.push(() => target.removeEventListener(type, fn as EventListener, opts))
  }

  private installListeners(): void {
    if (this.removers.length) return
    this.listen(window, 'keydown', (e: KeyboardEvent) => this.onKeyCapture(e), { capture: true, passive: true })
    this.listen(window, 'keydown', (e: KeyboardEvent) => this.onKeyBubble(e), { passive: true })
    this.listen(window, 'keyup', (e: KeyboardEvent) => { if (this.repeatCode === e.code) this.repeatCode = null }, { capture: true, passive: true })
    // a keyup lost to a focus change must not leave the dedupe stuck
    this.listen(window, 'blur', () => { this.repeatCode = null })
    this.listen(document, 'click', (e: MouseEvent) => this.onClick(e), { capture: true, passive: true })
    this.listen(window, 'pointermove', (e: PointerEvent) => {
      if (!this.visible || e.pointerType === 'touch') return
      this.acc.addPointerPx(Math.hypot(e.movementX || 0, e.movementY || 0))
    }, { passive: true })
    this.listen(document, 'visibilitychange', () => (document.visibilityState === 'hidden' ? this.onHidden() : this.onVisible()))
    this.listen(window, 'pagehide', () => { if (this.active) { this.closeTick(); this.flushSync() } })
    this.listen(window, 'focus', () => this.track('focus', { props: { state: 'focus' } }))
    this.listen(window, 'blur', () => this.track('focus', { props: { state: 'blur' } }))
    this.listen(window, 'online', () => { const u = this.session?.userId; if (u) void this.sender?.drain(u) })
    try {
      if (typeof PerformanceObserver !== 'undefined' && PerformanceObserver.supportedEntryTypes?.includes('longtask')) {
        this.longTaskObserver = new PerformanceObserver(list => {
          for (const entry of list.getEntries()) this.acc.addCompletion({ longTaskMs: entry.duration })
        })
        this.longTaskObserver.observe({ type: 'longtask', buffered: false })
      }
    } catch { /* unsupported */ }
  }

  private removeListeners(): void {
    for (const r of this.removers) r()
    this.removers = []
    this.longTaskObserver?.disconnect()
    this.longTaskObserver = null
  }

  private onHidden(): void {
    if (!this.active || this.signedOut) return
    this.closeTick()
    this.visible = false
    this.hiddenWall = this.clock.wall()
    this.record('visibility', { props: { state: 'hidden' } })
    this.stopTimers()
    this.flushSync()
  }

  private onVisible(): void {
    if (!this.active || this.signedOut) return
    this.visible = true
    const hiddenMs = this.hiddenWall === null ? 0 : this.clock.wall() - this.hiddenWall
    this.hiddenWall = null
    if (hiddenMs > ACTIVITY_SESSION_HIDDEN_GAP_MS) this.rollover('hidden_gap', false)
    else if (this.driftExceeded()) this.rollover('clock_drift', false)
    this.record('visibility', { props: { state: 'visible', hiddenMs: Math.round(hiddenMs) } })
    this.openWindow()
    this.startTimers()
    const userId = this.session?.userId
    void this.journal.migrateInto(this.store, () => !!this.sender?.isHalted).then(() => (userId ? this.sender?.drain(userId) : undefined)).catch(() => undefined)
  }

  private onClick(e: MouseEvent): void {
    const target = e.target
    if (!(target instanceof Element) || target.closest('.ol-viewport')) return
    this.count(target.closest('[data-activity-zone="toolbar"]') ? 'toolbarClicks' : 'panelClicks')
  }

  // ── Keyboard (§3.3) ─────────────────────────────────────────────────────────

  private popFrame(token: number): void {
    const top = this.frames[this.frames.length - 1]
    if (top && top.token === token) this.frames.pop()
  }

  private onKeyCapture(e: KeyboardEvent): void {
    // a stale key frame (propagation stopped before the bubble listener) never outlives the next keydown
    this.frames = this.frames.filter(f => f.token === null)
    this.dispatchTracked = false
    this.dispatchReported = false
    this.keyDispatch = null
    const ctx = this.ctx
    if (!ctx || !this.active) return
    const t = this.clock.t()
    if (e.isComposing || MODIFIER_KEYS.has(e.key)) return
    if (e.repeat) { this.acc.input(t, null); return }
    if (isActivationKeyOnControl(e)) { this.acc.input(t, null); return }
    if (isFormFieldTarget(e)) { this.onFormFieldKey(e, ctx, t); return }
    const name = this.shortcutOf(e, ctx)
    if (!name) { this.acc.input(t, null); return }
    const token = ++this.frameCounter
    this.frames.push({ trigger: { via: 'key', key: name }, token })
    this.keyDispatch = { name, token }
    setTimeout(() => this.popFrame(token), 0)
    this.acc.input(t, name === 'Space' ? null : 'shortcutKeys')
  }

  private onFormFieldKey(e: KeyboardEvent, ctx: { surface: ActivitySurface; shortcuts: boolean }, t: number): void {
    this.acc.input(t, 'typingKeys')
    const mono = this.clock.mono()
    const idle = mono - this.lastFormFieldKeyMono
    this.lastFormFieldKeyMono = mono
    if (e.ctrlKey || e.altKey || e.metaKey || !this.pointerOverMap) return
    if (idle < ACTIVITY_FORM_FIELD_IDLE_MS || mono - this.lastFormFieldEventMono < ACTIVITY_FORM_FIELD_RATE_MS) return
    if (!this.shortcutOf(e, ctx)) return // the key identity never leaves this function
    const field = (e.target as HTMLElement).tagName.toLowerCase() as 'input' | 'textarea' | 'select'
    this.lastFormFieldEventMono = mono
    this.track('shortcut.ignored', { props: { reason: 'form_field', field } })
  }

  private onKeyBubble(e: KeyboardEvent): void {
    const d = this.keyDispatch
    if (!d) return
    this.popFrame(d.token)
    this.keyDispatch = null
    if (e.repeat || this.dispatchTracked || this.dispatchReported) return
    this.dispatchReported = true
    this.track('shortcut.ignored', { trigger: { via: 'key', key: d.name }, props: { reason: 'not_applicable' } })
  }
}

/** Re-export so call sites need one import. */
export { VIA }
